import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Divider,
  Typography,
  Paper,
  Avatar,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close,
  ArrowBack,
  ArrowForward,
  Home,
  Person,
  Schedule,
  Assignment,
  CheckCircle,
  PauseCircle,
  PlayCircle,
  AccessTime,
  CalendarToday,
  LocationOn,
  Description,
  Image as ImageIcon,
  Inventory as InventoryIcon,
  PersonAdd,
  AccountCircle,
  AttachMoney,
  Add,
  Receipt,
  Visibility,
  PhotoCamera,
  Star,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatDate, formatDateTime } from '../../utils/i18nHelpers';

interface Flat {
  id: number;
  flat_number: string;
  building?: { id: number; name: string; name_ar?: string; latitude?: number; longitude?: number };
}

interface ServiceType {
  id: number;
  name_en: string;
  name_ar: string;
}

interface Technician {
  id: number;
  service_type: string;
  company?: string;
  user?: { id: number; name: string; name_ar?: string; email: string } | null;
  service_types?: ServiceType[];
}

interface User {
  id: number;
  name: string;
  name_ar?: string;
  email?: string;
}

interface PauseLog {
  id: number;
  action: 'pause' | 'resume';
  notes?: string;
  location_lat?: number | string;
  location_lng?: number | string;
  created_at: string;
  user?: User;
}

interface Expense {
  id: number;
  expense_number: string;
  title: string;
  description?: string;
  category?: string; // For backward compatibility
  service_type?: string;
  amount: number;
  expense_date: string;
  vendor?: string;
  payment_method?: string;
  notes?: string;
  receipts?: string[];
}

interface Amenity {
  id: number;
  name_en: string;
  name_ar: string;
  is_active?: boolean;
}

interface WorkOrderDetailData {
  id: number;
  work_order_number: string;
  title: string;
  title_ar?: string;
  description: string;
  priority: 'low' | 'medium' | 'urgent';
  status: 'pending' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
  scheduled_from?: string;
  scheduled_to?: string;
  completed_date?: string;
  notes?: string;
  images?: string[];
  flat?: Flat;
  flats?: Flat[];
  amenities?: Amenity[];
  technician?: Technician;
  assigned_user?: User;
  created_by?: User;
  items?: Array<{ id: number; item_name: string; category?: string; pivot?: { quantity: number } }>;
  created_at?: string;
  updated_at?: string;
  expenses?: Expense[];
  // After renaming backend Expenses -> PurchaseOrders, API now returns `purchase_orders`
  // Keep both fields for backward compatibility
  purchase_orders?: Expense[];
  check_ins?: Array<{
    id: number;
    check_in_time?: string;
    check_out_time?: string;
    before_images?: string[];
    after_images?: string[];
    check_in_location_lat?: number | string;
    check_in_location_lng?: number | string;
    check_out_location_lat?: number | string;
    check_out_location_lng?: number | string;
    check_in_notes?: string;
    check_out_notes?: string;
    location_lat?: number | string;
    location_lng?: number | string;
    notes?: string;
  }>;
  pauses?: PauseLog[];
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'error';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'default';
    default: return 'default';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed': return 'success';
    case 'in_progress': return 'info';
    case 'paused': return 'warning';
    case 'pending': return 'warning';
    case 'cancelled': return 'error';
    default: return 'default';
  }
};

// Using formatDateTime and formatDate from i18nHelpers instead

const formatDuration = (start?: string, end?: string, fallback?: string) => {
  if (!start || !end) return fallback || '';
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return fallback || '';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  } catch {
    return fallback || '';
  }
};

const InfoCard: React.FC<{ icon: React.ReactNode; label: string; value: string | React.ReactNode }> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ color: 'primary.main', mt: 0.5 }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const WorkOrderDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can, user, technicianId } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [wo, setWo] = useState<WorkOrderDetailData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [expenseFormData, setExpenseFormData] = useState({
    title: '',
    description: '',
    service_type: '',
    amount: 0,
    expense_date: new Date().toISOString().split('T')[0],
    vendor: '',
    notes: '',
  });
  const [serviceTypes, setServiceTypes] = useState<Array<{ id: number; name_en: string; name_ar: string }>>([]);
  const [technicianServiceType, setTechnicianServiceType] = useState<string | null>(null);
  const [expenseImages, setExpenseImages] = useState<File[]>([]);
  const [expenseImagePreviews, setExpenseImagePreviews] = useState<string[]>([]);
  const [expenseDetailsOpen, setExpenseDetailsOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [submittingExpense, setSubmittingExpense] = useState(false);
  
  // Camera capture state for expenses
  const [expenseCameraOpen, setExpenseCameraOpen] = useState(false);
  const [expenseStream, setExpenseStream] = useState<MediaStream | null>(null);
  const expenseVideoRef = useRef<HTMLVideoElement>(null);
  const expenseCanvasRef = useRef<HTMLCanvasElement>(null);

  const storageBase = (() => {
    const base = (api.defaults.baseURL as string) || '';
    return base.replace(/\/api\/?$/, '');
  })();
  const storageUrl = (path?: string) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    return `${storageBase}/storage/${path}`;
  };

  const isTechnicianUser = user?.role === 'technician' || !!technicianId;
  const assignedTechnicianServiceType = wo?.technician?.service_type || null;

  const openImagePreview = (images: string[], index: number) => {
    setPreviewImages(images.map(img => storageUrl(img)));
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const handlePrevImage = () => {
    setPreviewIndex((prev) => (prev > 0 ? prev - 1 : previewImages.length - 1));
  };

  const handleNextImage = () => {
    setPreviewIndex((prev) => (prev < previewImages.length - 1 ? prev + 1 : 0));
  };

  const fetchDetail = async () => {
    try {
      const res = await api.get(`/work-orders/${id}`);
      setWo(res.data);
    } catch (e) {
      setWo(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await api.get('/service-types');
      setServiceTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching service types:', error);
      setServiceTypes([]);
    }
  };

  const fetchTechnicianServiceType = async () => {
    // Check if user is a technician (either by role or technicianId)
    const isTechnician = user?.role === 'technician' || !!technicianId;
    if (!isTechnician) return;

    try {
      // Try to get technicianId from user if not available
      let techId = technicianId;
      if (!techId && user?.role === 'technician') {
        // Find technician by user_id
        const techResponse = await api.get('/technicians');
        const technicians = Array.isArray(techResponse.data) ? techResponse.data : [];
        const tech = technicians.find((t: any) => t.user?.id === user.id);
        if (tech) {
          techId = tech.id;
        }
      }
      
      if (techId) {
        const response = await api.get(`/technicians/${techId}`);
        if (response.data?.service_type) {
          const techServiceType = response.data.service_type;
          setTechnicianServiceType(techServiceType);
          
          // If service types are loaded, try to match; otherwise just use techServiceType
          let serviceTypeValue = techServiceType;
          if (serviceTypes.length > 0) {
            const matchingServiceType = serviceTypes.find(
              st => st.name_en === techServiceType
            );
            serviceTypeValue = matchingServiceType ? matchingServiceType.name_en : techServiceType;
          }

          // Update form data with the service type so it is preselected
          setExpenseFormData(prev => ({ ...prev, service_type: serviceTypeValue }));
          return serviceTypeValue;
        }
      }
    } catch (error) {
      console.error('Error fetching technician service type:', error);
    }
    
    return null;
  };

  const handleExpenseImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setExpenseImages((prev) => [...prev, ...filesArray]);
      const newPreviews = filesArray.map((file) => URL.createObjectURL(file));
      setExpenseImagePreviews((prev) => [...prev, ...newPreviews]);
    }
  };

  const handleRemoveExpenseImage = (index: number) => {
    setExpenseImages((prev) => prev.filter((_, i) => i !== index));
    setExpenseImagePreviews((prev) => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index]);
      return newPreviews.filter((_, i) => i !== index);
    });
  };

  // Camera capture functions for expenses
  const openExpenseCamera = async () => {
    setExpenseCameraOpen(true);
    try {
      // Try back camera first (for mobile)
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      } catch (envError) {
        // Fallback to any available camera
        console.log('Back camera not available, trying default camera');
        try {
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          });
        } catch (fallbackError) {
          // Last resort - any video
          mediaStream = await navigator.mediaDevices.getUserMedia({ 
            video: true
          });
        }
      }
      
      if (mediaStream) {
        setExpenseStream(mediaStream);
      } else {
        setExpenseCameraOpen(false);
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setExpenseCameraOpen(false);
    }
  };

  const closeExpenseCamera = () => {
    if (expenseStream) {
      expenseStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setExpenseStream(null);
    }
    setExpenseCameraOpen(false);
  };

  const captureExpensePhoto = () => {
    if (!expenseVideoRef.current || !expenseCanvasRef.current) return;
    
    const video = expenseVideoRef.current;
    const canvas = expenseCanvasRef.current;
    const context = canvas.getContext('2d');
    
    if (!context) return;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob: Blob | null) => {
      if (!blob) return;
      
      const file = new File([blob], `expense-receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setExpenseImages(prev => [...prev, file]);
      setExpenseImagePreviews(prev => [...prev, URL.createObjectURL(file)]);
      
      closeExpenseCamera();
    }, 'image/jpeg', 0.9);
  };

  // Effect to handle video stream when it changes
  useEffect(() => {
    if (expenseStream && expenseVideoRef.current) {
      const video = expenseVideoRef.current;
      video.srcObject = expenseStream;
      video.play().catch((error: any) => {
        console.error('Error playing video:', error);
      });
    } else if (expenseVideoRef.current && !expenseStream) {
      expenseVideoRef.current.srcObject = null;
    }
  }, [expenseStream, expenseCameraOpen]);

  const handleExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingExpense) return; // Prevent double submission
    setSubmittingExpense(true);
    try {
      const formData = new FormData();
      formData.append('work_order_id', id!);
      formData.append('title', expenseFormData.title);
      formData.append('description', expenseFormData.description);
      formData.append('service_type', expenseFormData.service_type);
      formData.append('amount', expenseFormData.amount.toString());
      formData.append('expense_date', expenseFormData.expense_date);
      formData.append('vendor', expenseFormData.vendor);
      formData.append('notes', expenseFormData.notes);
      
      expenseImages.forEach((file) => {
        formData.append('invoice_images[]', file);
      });

      await api.post('/expenses', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Reset form
      setExpenseFormData({
        title: '',
        description: '',
        service_type: technicianServiceType || '',
        amount: 0,
        expense_date: new Date().toISOString().split('T')[0],
        vendor: '',
        notes: '',
      });
      setExpenseImages([]);
      setExpenseImagePreviews([]);
      setExpenseDialogOpen(false);
      
      // Refresh work order details
      fetchDetail();
    } catch (error) {
      console.error('Error creating expense:', error);
    } finally {
      setSubmittingExpense(false);
    }
  };

  useEffect(() => {
    if (!previewOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        setPreviewIndex((prev) => (prev > 0 ? prev - 1 : previewImages.length - 1));
      }
      if (e.key === 'ArrowRight') {
        setPreviewIndex((prev) => (prev < previewImages.length - 1 ? prev + 1 : 0));
      }
      if (e.key === 'Escape') {
        setPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewOpen, previewImages.length]);

  useEffect(() => {
    fetchDetail();
    fetchServiceTypes();
    // Note: fetchTechnicianServiceType is called in a separate useEffect that waits for serviceTypes
    
    // Scroll to expenses section if hash is present
    if (window.location.hash === '#expenses') {
      setTimeout(() => {
        const expensesSection = document.getElementById('expenses-section');
        if (expensesSection) {
          expensesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
    
    // Open expense dialog if hash is #add-expense
    if (window.location.hash === '#add-expense') {
      setTimeout(() => {
        setExpenseDialogOpen(true);
        // Remove hash from URL after opening dialog
        window.history.replaceState(null, '', window.location.pathname);
      }, 500);
    }
  }, [id]);

  // Fetch technician service type once user/technician is known
  useEffect(() => {
    const isTechnician = user?.role === 'technician' || !!technicianId;
    if (user && isTechnician) {
      fetchTechnicianServiceType();
    }
  }, [technicianId, user]);

  // Sync locked service type (from technician user or assigned technician on work order)
  useEffect(() => {
    const isTechnician = user?.role === 'technician' || !!technicianId;
    const lockedFromUser = isTechnician ? technicianServiceType : null;
    const lockedFromWorkOrder = assignedTechnicianServiceType;
    const lockedServiceType = lockedFromUser || lockedFromWorkOrder || null;

    if (lockedServiceType && expenseFormData.service_type !== lockedServiceType) {
      setExpenseFormData(prev => ({ ...prev, service_type: lockedServiceType }));
    }
  }, [technicianServiceType, assignedTechnicianServiceType, user, technicianId, expenseFormData.service_type]);

  // Ensure service type is set when expense dialog opens for technicians
  useEffect(() => {
    const isTechnician = user?.role === 'technician' || !!technicianId;
    if (expenseDialogOpen && user && isTechnician) {
      // Re-fetch and set technician service type when dialog opens
      fetchTechnicianServiceType().then((serviceTypeValue) => {
        if (serviceTypeValue) {
          setExpenseFormData(prev => ({ ...prev, service_type: serviceTypeValue }));
        }
      });
    }
  }, [expenseDialogOpen, serviceTypes, user, technicianId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!wo) {
    return (
      <Box>
        <Typography variant="h6">{t('workOrderDetail.notFound.title')}</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(-1)} variant="outlined">
          {t('workOrderDetail.notFound.back')}
        </Button>
      </Box>
    );
  }

  // Normalize expenses / purchase orders coming from backend
  const expenses: Expense[] = (wo.purchase_orders && wo.purchase_orders.length
    ? wo.purchase_orders
    : wo.expenses || []);

  const latestCheckIn = wo.check_ins && wo.check_ins.length > 0 ? wo.check_ins[0] : null;
  const workDuration = latestCheckIn && latestCheckIn.check_in_time && latestCheckIn.check_out_time
    ? formatDuration(latestCheckIn.check_in_time, latestCheckIn.check_out_time, t('workOrderDetail.workDuration.label'))
    : t('workOrderDetail.workDuration.label');

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>{language === 'ar' && wo.title_ar ? wo.title_ar : wo.title}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
            {t('workOrderDetail.header.workOrderNumber').replace('{{number}}', wo.work_order_number)}
          </Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap" mt={1.5} sx={{ alignItems: 'center' }}>
            <Chip
              label={t(`workOrders.priority.${wo.priority}`)}
              color={getPriorityColor(wo.priority) as any}
              size="small"
              sx={{ fontWeight: 600, minWidth: 'fit-content' }}
            />
            <Chip
              label={t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`)}
              color={getStatusColor(wo.status) as any}
              size="small"
              sx={{ fontWeight: 600, minWidth: 'fit-content' }}
            />
          </Box>
        </Box>
        <Button variant="outlined" onClick={() => navigate(-1)} startIcon={<ArrowBack />}>
          {t('workOrderDetail.header.back')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left Column - Main Information */}
        <Box sx={{ flex: { md: '2 1 0%' }, minWidth: 0 }}>
          {/* Description */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Description color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('workOrderDetail.section.description')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                {wo.description}
              </Typography>
            </CardContent>
          </Card>

          {/* Key Information Grid */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('workOrderDetail.section.keyInformation')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <InfoCard
                  icon={<Home />}
                  label={t('workOrderDetail.info.location')}
                  value={wo.flat?.building?.name && (wo.flats?.length || wo.flat?.flat_number)
                    ? t('workOrderDetail.info.locationValue')
                        .replace('{{building}}', language === 'ar' && wo.flat.building.name_ar ? wo.flat.building.name_ar : wo.flat.building.name)
                        .replace('{{flat}}', wo.flats && wo.flats.length > 0 ? wo.flats.map(f => f.flat_number).join(', ') : (wo.flat?.flat_number || ''))
                    : t('workOrderDetail.info.notAssigned')}
                />
                {wo.amenities && wo.amenities.length > 0 && (
                  <InfoCard
                    icon={<Star />}
                    label={t('workOrderDetail.info.amenities')}
                    value={wo.amenities.map(a => language === 'ar' ? a.name_ar : a.name_en).join(', ')}
                  />
                )}
                <InfoCard
                  icon={<Assignment />}
                  label={t('workOrderDetail.info.assignedTo')}
                  value={wo.technician?.user?.name
                    ? `${language === 'ar' && wo.technician.user.name_ar ? wo.technician.user.name_ar : wo.technician.user.name} (${
                        wo.technician.service_types && wo.technician.service_types.length > 0
                          ? (language === 'ar' ? wo.technician.service_types[0].name_ar : wo.technician.service_types[0].name_en)
                          : wo.technician.service_type
                      })`
                    : (wo.technician?.service_types && wo.technician.service_types.length > 0
                        ? (language === 'ar' ? wo.technician.service_types[0].name_ar : wo.technician.service_types[0].name_en)
                        : wo.technician?.service_type) || t('workOrderDetail.info.notAssigned')}
                />
                <InfoCard
                  icon={<Person />}
                  label={t('workOrderDetail.info.providerType')}
                  value={wo.technician?.service_types && wo.technician.service_types.length > 0
                    ? (language === 'ar' ? wo.technician.service_types[0].name_ar : wo.technician.service_types[0].name_en)
                    : wo.technician?.service_type || t('workOrderDetail.info.notAssigned')}
                />
                <InfoCard
                  icon={<AccountCircle />}
                  label={t('workOrderDetail.info.createdBy')}
                  value={(language === 'ar' && wo.created_by?.name_ar ? wo.created_by.name_ar : wo.created_by?.name) || t('workOrderDetail.info.notAvailable')}
                />
                <InfoCard
                  icon={<CalendarToday />}
                  label={t('workOrderDetail.info.createdOn')}
                  value={formatDateTime(wo.created_at, language)}
                />
                <InfoCard
                  icon={<Schedule />}
                  label={t('workOrderDetail.info.scheduledFrom')}
                  value={formatDateTime(wo.scheduled_from, language)}
                />
                <InfoCard
                  icon={<Schedule />}
                  label={t('workOrderDetail.info.scheduledTo')}
                  value={formatDateTime(wo.scheduled_to, language)}
                />
                {wo.completed_date && (
                  <InfoCard
                    icon={<CheckCircle />}
                    label={t('workOrderDetail.info.completedDate')}
                    value={formatDate(wo.completed_date, language)}
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Check-In / Check-Out Details */}
          {latestCheckIn && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <CheckCircle color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('workOrderDetail.section.checkDetails')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  {/* Check-In */}
                  {latestCheckIn.check_in_time && (
                    <Box>
                      <Paper sx={{ p: 2.5, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                          {t('workOrderDetail.checkIn.title')}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('workOrderDetail.check.time')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateTime(latestCheckIn.check_in_time, language)}
                          </Typography>
                        </Box>
                        {latestCheckIn.check_in_notes && (
                          <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('workOrderDetail.check.notes')}
                          </Typography>
                            <Typography variant="body2">{latestCheckIn.check_in_notes}</Typography>
                          </Box>
                        )}
                        {latestCheckIn.check_in_location_lat && latestCheckIn.check_in_location_lng && (
                          <Box sx={{ mt: 2 }}>
                            <iframe
                              title="checkin-map"
                              width="100%"
                              height="150"
                              style={{ border: 0, borderRadius: 8 }}
                              loading="lazy"
                              allowFullScreen
                              referrerPolicy="no-referrer-when-downgrade"
                              src={`https://www.google.com/maps?q=${encodeURIComponent(String(latestCheckIn.check_in_location_lat))},${encodeURIComponent(String(latestCheckIn.check_in_location_lng))}&z=15&output=embed`}
                            />
                          </Box>
                        )}
                        {latestCheckIn.before_images && latestCheckIn.before_images.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {t('workOrderDetail.check.beforeImages')
                                .replace('{{count}}', String(latestCheckIn.before_images.length))}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {latestCheckIn.before_images.map((img, i) => (
                                <img
                                  key={i}
                                  src={storageUrl(img)}
                                  alt={`before-${i}`}
                                  onClick={() => openImagePreview(latestCheckIn.before_images || [], i)}
                                  style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 6,
                                    border: '1px solid #444',
                                    cursor: 'pointer',
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Check-Out */}
                  {latestCheckIn.check_out_time && (
                    <Box>
                      <Paper sx={{ p: 2.5, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'info.main' }}>
                          {t('workOrderDetail.checkOut.title')}
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('workOrderDetail.check.time')}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDateTime(latestCheckIn.check_out_time, language)}
                          </Typography>
                        </Box>
                        {latestCheckIn.check_out_notes && (
                          <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('workOrderDetail.check.notes')}
                          </Typography>
                            <Typography variant="body2">{latestCheckIn.check_out_notes}</Typography>
                          </Box>
                        )}
                        {latestCheckIn.check_out_location_lat && latestCheckIn.check_out_location_lng && (
                          <Box sx={{ mt: 2 }}>
                            <iframe
                              title="checkout-map"
                              width="100%"
                              height="150"
                              style={{ border: 0, borderRadius: 8 }}
                              loading="lazy"
                              allowFullScreen
                              referrerPolicy="no-referrer-when-downgrade"
                              src={`https://www.google.com/maps?q=${encodeURIComponent(String(latestCheckIn.check_out_location_lat))},${encodeURIComponent(String(latestCheckIn.check_out_location_lng))}&z=15&output=embed`}
                            />
                          </Box>
                        )}
                        {latestCheckIn.after_images && latestCheckIn.after_images.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {t('workOrderDetail.check.afterImages')
                                .replace('{{count}}', String(latestCheckIn.after_images.length))}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {latestCheckIn.after_images.map((img, i) => (
                                <img
                                  key={i}
                                  src={storageUrl(img)}
                                  alt={`after-${i}`}
                                  onClick={() => openImagePreview(latestCheckIn.after_images || [], i)}
                                  style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 6,
                                    border: '1px solid #444',
                                    cursor: 'pointer',
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  )}
                </Box>

                {/* Work Duration */}
                {latestCheckIn.check_in_time && latestCheckIn.check_out_time && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.dark', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {t('workOrderDetail.workDuration.label')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                      {workDuration}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Pause/Resume Timeline */}
          {wo.pauses && wo.pauses.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <PauseCircle color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('workOrderDetail.pauseResume.title')}
                  </Typography>
                </Box>
                <Box sx={{ position: 'relative', pl: 3 }}>
                  {wo.pauses.map((pause, index) => (
                    <Box key={pause.id} sx={{ position: 'relative', pb: 3 }}>
                      {/* Timeline line */}
                      {index < wo.pauses!.length - 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -15,
                            top: 40,
                            bottom: -12,
                            width: 2,
                            bgcolor: 'divider',
                          }}
                        />
                      )}
                      {/* Timeline dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -20,
                          top: 4,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: pause.action === 'pause' ? 'warning.main' : 'success.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '2px solid',
                          borderColor: 'background.paper',
                          zIndex: 1,
                        }}
                      >
                        {pause.action === 'pause' ? (
                          <PauseCircle sx={{ fontSize: 18, color: 'white' }} />
                        ) : (
                          <PlayCircle sx={{ fontSize: 18, color: 'white' }} />
                        )}
                      </Box>
                      {/* Content */}
                      <Paper sx={{ p: 2, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', ml: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                              {pause.action === 'pause'
                                ? t('workOrderDetail.pauseResume.paused')
                                : t('workOrderDetail.pauseResume.resumed')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDateTime(pause.created_at, language)}
                            </Typography>
                          </Box>
                          {pause.user && (
                            <Chip
                              label={language === 'ar' && pause.user.name_ar ? pause.user.name_ar : pause.user.name}
                              size="small"
                              avatar={<Avatar sx={{ width: 20, height: 20 }}>{(language === 'ar' && pause.user.name_ar ? pause.user.name_ar : pause.user.name).charAt(0)}</Avatar>}
                            />
                          )}
                        </Box>
                        {pause.notes && (
                          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                            {pause.notes}
                          </Typography>
                        )}
                        {pause.location_lat && pause.location_lng && (
                          <Box sx={{ mt: 2 }}>
                            <iframe
                              title={`pause-map-${pause.id}`}
                              width="100%"
                              height="120"
                              style={{ border: 0, borderRadius: 8 }}
                              loading="lazy"
                              allowFullScreen
                              referrerPolicy="no-referrer-when-downgrade"
                              src={`https://www.google.com/maps?q=${encodeURIComponent(String(pause.location_lat))},${encodeURIComponent(String(pause.location_lng))}&z=15&output=embed`}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Assigned Items */}
          {wo.items && wo.items.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                <InventoryIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('workOrderDetail.section.assignedInventory')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {wo.items.map(it => (
                    <Chip 
                      key={it.id} 
                      label={`${it.item_name} x ${it.pivot?.quantity ?? 1}`}
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Expenses Section */}
          <Card id="expenses-section" sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AttachMoney color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('workOrderDetail.section.expenses')}
                  </Typography>
                  {expenses && expenses.length > 0 && (
                    <Chip 
                      label={t('workOrderDetail.expenses.badge')
                        .replace('{{count}}', String(expenses.length))
                        .replace('{{plural}}', expenses.length > 1 ? 's' : '')}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                {(can('expenses.add') || can('work-orders.add-expense')) && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={async () => {
                      // Ensure technician service type is fetched and set before opening dialog
                      const isTechnician = user?.role === 'technician' || !!technicianId;
                      if (user && isTechnician && serviceTypes.length > 0) {
                        await fetchTechnicianServiceType();
                      }
                      setExpenseDialogOpen(true);
                    }}
                  >
                    {t('workOrders.actions.addExpense')}
                  </Button>
                )}
              </Box>
              {expenses && expenses.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {expenses.map((expense) => (
                    <Paper key={expense.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {expense.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{expense.expense_number}
                          </Typography>
                        </Box>
                        <Chip
                          label={formatCurrency(expense.amount || 0, language)}
                          color="primary"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
                        {(expense.service_type || expense.category) && (
                          <Chip
                            label={(expense.service_type || expense.category || '').toUpperCase()}
                            size="small"
                            variant="outlined"
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(expense.expense_date, language)}
                        </Typography>
                        {expense.vendor && (
                          <Typography variant="body2" color="text.secondary">
                            {t('workOrderDetail.expenses.vendor').replace('{{vendor}}', expense.vendor)}
                          </Typography>
                        )}
                      </Box>
                      {expense.description && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                          {expense.description}
                        </Typography>
                      )}
                      {expense.receipts && expense.receipts.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {expense.receipts.map((receipt, idx) => (
                            <img
                              key={idx}
                              src={storageUrl(receipt)}
                              alt={`Receipt ${idx + 1}`}
                              onClick={() => openImagePreview(expense.receipts || [], idx)}
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: 'cover',
                                borderRadius: 6,
                                border: '1px solid #444',
                                cursor: 'pointer',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => {
                            setSelectedExpense(expense);
                            setExpenseDetailsOpen(true);
                          }}
                        >
                          {t('workOrderDetail.expenses.viewDetails')}
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.dark', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                      {t('workOrderDetail.expenses.total')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
                      {formatCurrency(expenses.reduce((sum, e) => sum + parseFloat(String(e.amount || 0)), 0), language)}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  {t('workOrderDetail.expenses.none')}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Work Order Images */}
          {wo.images && wo.images.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ImageIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('workOrderDetail.section.images')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {wo.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={storageUrl(img)}
                      alt={`wo-${idx}`}
                      onClick={() => openImagePreview(wo.images || [], idx)}
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid #444',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Additional Notes */}
          {wo.notes && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('workOrderDetail.section.additionalNotes')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  {wo.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Right Column - Summary */}
        <Box sx={{ flex: { md: '1 1 0%' }, minWidth: 0 }}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {t('workOrderDetail.section.summary')}
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('workOrderDetail.summary.status')}
                  </Typography>
                  <Chip
                    label={t(`workOrders.status.${wo.status === 'in_progress' ? 'inProgress' : wo.status === 'paused' ? 'paused' : wo.status}`)}
                    color={getStatusColor(wo.status) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('workOrderDetail.summary.priority')}
                  </Typography>
                  <Chip
                    label={t(`workOrders.priority.${wo.priority}`)}
                    color={getPriorityColor(wo.priority) as any}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Divider />
                {latestCheckIn && latestCheckIn.check_in_time && latestCheckIn.check_out_time && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('workOrderDetail.summary.workDuration')}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600, color: 'primary.main' }}>
                        {workDuration}
                      </Typography>
                    </Box>
                    <Divider />
                  </>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('workOrderDetail.summary.created')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateTime(wo.created_at, language)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('workOrderDetail.summary.lastUpdated')}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {formatDateTime(wo.updated_at, language)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Image Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            m: 0,
          },
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            minHeight: '70vh',
            minWidth: '70vw',
          }}
        >
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>

          {previewImages.length > 1 && (
            <>
              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 8,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 1,
                }}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 1,
                }}
              >
                <ArrowForward />
              </IconButton>
            </>
          )}

          {previewImages.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <img
                src={previewImages[previewIndex]}
                alt={`Preview ${previewIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
              {previewImages.length > 1 && (
                <Typography
                  variant="body2"
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    color: 'white',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  {t('workOrderDetail.preview.counter')
                    .replace('{{current}}', String(previewIndex + 1))
                    .replace('{{total}}', String(previewImages.length))}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog 
        open={expenseDialogOpen} 
        onClose={() => {
          setExpenseDialogOpen(false);
          // Reset form but keep service type for technicians
          setExpenseFormData({
            title: '',
            description: '',
            service_type: technicianServiceType || '',
            amount: 0,
            expense_date: new Date().toISOString().split('T')[0],
            vendor: '',
            notes: '',
          });
          setExpenseImages([]);
          setExpenseImagePreviews([]);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {t('workOrderDetail.expenseDialog.title')}
          <IconButton
            aria-label="close"
            onClick={() => {
              setExpenseDialogOpen(false);
              // Reset form but keep service type for technicians
              setExpenseFormData({
                title: '',
                description: '',
                service_type: technicianServiceType || '',
                amount: 0,
                expense_date: new Date().toISOString().split('T')[0],
                vendor: '',
                notes: '',
              });
              setExpenseImages([]);
              setExpenseImagePreviews([]);
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleExpenseSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label={t('workOrderDetail.expenseDialog.fields.title')}
                required
                value={expenseFormData.title}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, title: e.target.value })}
              />
              <TextField
                fullWidth
                label={t('workOrderDetail.expenseDialog.fields.description')}
                multiline
                rows={2}
                value={expenseFormData.description}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, description: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                {(() => {
                  // Determine if service type should be locked and displayed read-only
                  const isTechnician = isTechnicianUser;
                  const lockedFromUser = isTechnician ? technicianServiceType : null;
                  const lockedFromWorkOrder = assignedTechnicianServiceType;
                  const lockedServiceType = lockedFromUser || lockedFromWorkOrder || '';

                  if (lockedServiceType) {
                    // Map locked service type (stored as English name_en) to localized label
                    const serviceTypeObj = serviceTypes.find(st => st.name_en === lockedServiceType);
                    const displayServiceType = serviceTypeObj
                      ? (language === 'ar' ? serviceTypeObj.name_ar : serviceTypeObj.name_en)
                      : lockedServiceType;

                    return (
                      <TextField
                        fullWidth
                        label={t('workOrderDetail.expenseDialog.fields.serviceType') || 'Service Type'}
                        value={displayServiceType}
                        disabled
                      />
                    );
                  }

                  // No locked service type: allow manual selection
                  return (
                    <FormControl fullWidth>
                      <InputLabel>{t('workOrderDetail.expenseDialog.fields.serviceType') || 'Service Type'}</InputLabel>
                      <Select
                        value={expenseFormData.service_type}
                        label={t('workOrderDetail.expenseDialog.fields.serviceType') || 'Service Type'}
                        onChange={(e) => setExpenseFormData({ ...expenseFormData, service_type: e.target.value })}
                      >
                        {serviceTypes.map((serviceType) => (
                          <MenuItem key={serviceType.id} value={serviceType.name_en}>
                            {language === 'ar' ? serviceType.name_ar : serviceType.name_en}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                })()}
                <TextField
                  fullWidth
                  label={t('workOrderDetail.expenseDialog.fields.amount')}
                  type="number"
                  required
                  inputProps={{ min: 0, step: "0.01" }}
                  value={expenseFormData.amount}
                  onChange={(e) => setExpenseFormData({ ...expenseFormData, amount: parseFloat(e.target.value) || 0 })}
                />
              </Box>
              <TextField
                fullWidth
                label={t('workOrderDetail.expenseDialog.fields.expenseDate')}
                type="date"
                required
                InputLabelProps={{ shrink: true }}
                value={expenseFormData.expense_date}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, expense_date: e.target.value })}
              />
              <TextField
                fullWidth
                label={t('workOrderDetail.expenseDialog.fields.vendor')}
                value={expenseFormData.vendor}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, vendor: e.target.value })}
              />
              <TextField
                fullWidth
                label={t('workOrderDetail.expenseDialog.fields.notes')}
                multiline
                rows={2}
                value={expenseFormData.notes}
                onChange={(e) => setExpenseFormData({ ...expenseFormData, notes: e.target.value })}
              />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('workOrderDetail.expenseDialog.invoice.label')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    id="expense-image-upload"
                    onChange={handleExpenseImageChange}
                  />
                  <label htmlFor="expense-image-upload" style={{ flex: 1 }}>
                    <Button variant="outlined" component="span" startIcon={<Receipt />} fullWidth>
                      {t('workOrderDetail.expenseDialog.invoice.button')}
                    </Button>
                  </label>
                  <Button 
                    variant="outlined" 
                    startIcon={<PhotoCamera />} 
                    onClick={openExpenseCamera}
                    sx={{ flex: 1 }}
                  >
                    {t('workOrderDetail.expenseDialog.invoice.camera')}
                  </Button>
                </Box>
                {expenseImagePreviews.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                    {expenseImagePreviews.map((preview, index) => (
                      <Box key={index} sx={{ position: 'relative', width: 100, height: 100 }}>
                        <img
                          src={preview.startsWith('blob:') || preview.startsWith('data:') ? preview : storageUrl(preview)}
                          alt={`Preview ${index + 1}`}
                          onClick={() => {
                            setPreviewImages(expenseImagePreviews.map(p => p.startsWith('blob:') || p.startsWith('data:') ? p : storageUrl(p)));
                            setPreviewIndex(index);
                            setPreviewOpen(true);
                          }}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 4,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveExpenseImage(index)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setExpenseDialogOpen(false);
              // Reset form but keep service type for technicians
              setExpenseFormData({
                title: '',
                description: '',
                service_type: technicianServiceType || '',
                amount: 0,
                expense_date: new Date().toISOString().split('T')[0],
                vendor: '',
                notes: '',
              });
              setExpenseImages([]);
              setExpenseImagePreviews([]);
            }} disabled={submittingExpense}>
              {t('workOrderDetail.expenseDialog.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={submittingExpense} startIcon={submittingExpense ? <CircularProgress size={20} /> : null}>
              {t('workOrderDetail.expenseDialog.submit')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Expense Details Dialog */}
      <Dialog 
        open={expenseDetailsOpen} 
        onClose={() => {
          setExpenseDetailsOpen(false);
          setSelectedExpense(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {t('workOrderDetail.expenseDetails.title')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setExpenseDetailsOpen(false);
                setSelectedExpense(null);
              }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedExpense && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('workOrderDetail.expenseDetails.expenseNumber')}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  #{selectedExpense.expense_number}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('workOrderDetail.expenseDetails.title')}
                </Typography>
                <Typography variant="body1">
                  {selectedExpense.title}
                </Typography>
              </Box>

              {selectedExpense.description && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('workOrderDetail.expenseDetails.description')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedExpense.description}
                    </Typography>
                  </Box>
                </>
              )}

              {selectedExpense.notes && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('workOrderDetail.expenseDetails.notes')}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedExpense.notes}
                    </Typography>
                  </Box>
                </>
              )}

              <Divider />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('workOrderDetail.expenseDetails.serviceType')}
                  </Typography>
                  <Typography variant="body1">
                    {(selectedExpense.service_type || selectedExpense.category || '-').toUpperCase()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('workOrderDetail.expenseDetails.amount')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatCurrency(selectedExpense.amount || 0, language)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('workOrderDetail.expenseDetails.date')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedExpense.expense_date, language)}
                  </Typography>
                </Box>

                {selectedExpense.vendor && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('workOrderDetail.expenseDetails.vendor')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedExpense.vendor}
                    </Typography>
                  </Box>
                )}

                {selectedExpense.payment_method && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('workOrderDetail.expenseDetails.paymentMethod')}
                    </Typography>
                    <Typography variant="body1">
                      {selectedExpense.payment_method}
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedExpense.receipts && selectedExpense.receipts.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                      {t('workOrderDetail.expenseDetails.receipts')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {selectedExpense.receipts.map((receipt, idx) => (
                        <img
                          key={idx}
                          src={storageUrl(receipt)}
                          alt={`Receipt ${idx + 1}`}
                          onClick={() => openImagePreview(selectedExpense.receipts || [], idx)}
                          style={{
                            width: 150,
                            height: 150,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExpenseDetailsOpen(false);
            setSelectedExpense(null);
          }}>
            {t('workOrderDetail.expenseDetails.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Expense Camera Dialog */}
      <Dialog
        open={expenseCameraOpen}
        onClose={closeExpenseCamera}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'black',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{t('workOrderDetail.expenseDialog.camera.title')}</Typography>
          <IconButton onClick={closeExpenseCamera} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {expenseStream ? (
            <>
              <video
                ref={expenseVideoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  maxHeight: '600px',
                  borderRadius: 8,
                }}
              />
              <canvas ref={expenseCanvasRef} style={{ display: 'none' }} />
            </>
          ) : (
            <Typography variant="body1" sx={{ color: 'white', py: 4 }}>
              {t('workOrderDetail.expenseDialog.camera.loading')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button onClick={closeExpenseCamera} variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
            {t('workOrderDetail.expenseDialog.camera.cancel')}
          </Button>
          <Button
            onClick={captureExpensePhoto}
            variant="contained"
            disabled={!expenseStream}
            startIcon={<PhotoCamera />}
          >
            {t('workOrderDetail.expenseDialog.camera.capture')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrderDetail;
